/**
 * @file       Module text-case directive
 * @version    1.0.0
 * @author     Cursor Agent
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 */
import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, 
    Optional } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ModuleTextCase, ModuleTextCaseRule, PreferenceService } 
    from '@app/service/preference.service';
import { CyString } from '@app/secure/service/CyString.service';

@Directive
({
    selector: '[appModuleTextCase]',
    standalone: false
})

/**
 * @desc   - applies module text-case rule to input and textarea controls
 */
class ModuleTextCaseDirective implements OnInit, OnDestroy
{
    @Input('appModuleTextCase') moduleName: ModuleTextCase;

    private currentRule: ModuleTextCaseRule;
    private subscriptions: Subscription[];

    constructor
    (
        private el: ElementRef,
        private preferenceService: PreferenceService,
        @Optional() private ngControl: NgControl
    )
    {
        this.currentRule = 'lower';
        this.subscriptions = [];
    }

    public ngOnInit(): void
    {
        let subscription: Subscription;

        subscription = this.preferenceService.preferences$.subscribe(() =>
        {
            this.currentRule = this.preferenceService.getModuleTextCaseRule(
                this.moduleName
            );
            this.applyRuleToElementValue();
        });
        this.subscriptions.push(subscription);
    }

    public ngOnDestroy(): void
    {
        this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
        this.subscriptions = [];
    }

    @HostListener('input', ['$event'])
    public onInput
    (
        event: Event
    ): void
    {
        let target: HTMLInputElement | HTMLTextAreaElement;
        let startPos: number;
        let endPos: number;
        let transformed: string;

        target = event.target as HTMLInputElement | HTMLTextAreaElement;
        if (!target)
        {
            return;
        }

        startPos = target.selectionStart || 0;
        endPos = target.selectionEnd || 0;
        transformed = this.transformValue(target.value);

        if (transformed !== target.value)
        {
            target.value = transformed;
            target.setSelectionRange(startPos, endPos);
            this.setControlValue(transformed);
        }
        else
        {
            this.setControlValue(target.value);
        }
    }

    @HostListener('paste', ['$event'])
    public onPaste
    (
        event: ClipboardEvent
    ): void
    {
        let target: HTMLInputElement | HTMLTextAreaElement;
        let pasted: string;
        let startPos: number;
        let endPos: number;
        let nextValue: string;
        let transformed: string;
        let cursorPos: number;

        target = event.target as HTMLInputElement | HTMLTextAreaElement;
        if (!target || !event.clipboardData)
        {
            return;
        }

        pasted = event.clipboardData.getData('text') || '';
        startPos = target.selectionStart || 0;
        endPos = target.selectionEnd || 0;
        nextValue = target.value.substring(0, startPos) + pasted +
            target.value.substring(endPos);
        transformed = this.transformValue(nextValue);
        cursorPos = startPos + pasted.length;

        if (transformed !== nextValue)
        {
            cursorPos = startPos + this.transformValue(pasted).length;
        }

        event.preventDefault();
        target.value = transformed;
        target.setSelectionRange(cursorPos, cursorPos);
        this.setControlValue(transformed);
    }

    private applyRuleToElementValue(): void
    {
        let input: HTMLInputElement | HTMLTextAreaElement;
        let transformed: string;

        input = this.el.nativeElement;
        if (!input || typeof input.value !== 'string')
        {
            return;
        }

        transformed = this.transformValue(input.value);
        if (transformed !== input.value)
        {
            input.value = transformed;
            this.setControlValue(transformed);
        }
    }

    private transformValue
    (
        value: string
    ): string
    {
        let result: string;

        result = value || '';

        if (this.currentRule === 'upper')
        {
            result = result.toUpperCase();
        }
        else if (this.currentRule === 'capitalize')
        {
            result = CyString.capitalizeSentence(result);
        }

        return result;
    }

    private setControlValue
    (
        value: string
    ): void
    {
        if (this.ngControl?.control)
        {
            this.ngControl.control.setValue(value, { emitEvent: false });
        }
    }
}

export { ModuleTextCaseDirective };
